/**
 * Typed fetch wrappers for the pplenglish API. Phase 3 only defines this; the
 * Phase 4 login/sync flow consumes it.
 */

import type { Direction, LearningDirection, LearningMode, ProgressRecord, ThemePref } from "./types";

const API_BASE = "/api/v1";

const CSRF_HEADER = { "X-Requested-With": "pplenglish" };

export type ApiErrorKind =
  | "network"
  | "unauthenticated"
  | "forbidden"
  | "rate_limited"
  | "server"
  | "validation";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  constructor(kind: ApiErrorKind, message: string, status: number | null = null) {
    super(message);
    this.kind = kind;
    this.status = status;
  }
}

interface RequestOptions {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

async function call<T>(path: string, opts: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { ...CSRF_HEADER };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method,
      credentials: "same-origin",
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    throw new ApiError("network", err instanceof Error ? err.message : String(err));
  }

  if (res.status === 204) return undefined as T;
  if (res.status === 401) throw new ApiError("unauthenticated", "unauthenticated", 401);
  if (res.status === 403) throw new ApiError("forbidden", "forbidden", 403);
  if (res.status === 429) throw new ApiError("rate_limited", "rate_limited", 429);
  if (res.status >= 400) {
    // Extract the server's `error: "<code>"` payload regardless of 4xx/5xx
    // so callers (e.g. /generate-examples 503 ai_disabled) can branch on the
    // semantic code instead of the raw status. Falls back to server_error_5xx
    // / error_4xx if the body is missing or not JSON.
    let detail: string | undefined;
    try {
      detail = (await res.clone().json() as { error?: string }).error;
    } catch {
      detail = undefined;
    }
    const kind: ApiErrorKind = res.status >= 500 ? "server" : "validation";
    throw new ApiError(
      kind,
      detail ?? (res.status >= 500 ? `server_error_${res.status}` : `error_${res.status}`),
      res.status,
    );
  }

  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return undefined as T;
}

export interface VocabFetchEntry {
  english: string;
  german: string;
  letter: string;
  abbreviation: string | null;
  examples: string[];
}

export type VocabFetchResult =
  | { kind: "fresh"; vocab: VocabFetchEntry[]; etag: string; count: number }
  | { kind: "not_modified"; etag: string };

/**
 * Fetch the public vocab list. If `ifNoneMatch` is set, the server may
 * respond 304 — we return a `not_modified` result instead of a body so the
 * caller knows the cached vocab is still good.
 */
export async function fetchVocab(ifNoneMatch?: string): Promise<VocabFetchResult> {
  const headers: Record<string, string> = { ...CSRF_HEADER };
  if (ifNoneMatch) headers["If-None-Match"] = ifNoneMatch;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/vocab`, {
      method: "GET",
      credentials: "same-origin",
      headers,
    });
  } catch (err) {
    throw new ApiError("network", err instanceof Error ? err.message : String(err));
  }

  const etag = res.headers.get("etag") ?? "";
  if (res.status === 304) {
    return { kind: "not_modified", etag };
  }
  if (res.status >= 400) {
    throw new ApiError("server", `vocab_fetch_${res.status}`, res.status);
  }
  const body = (await res.json()) as { vocab: VocabFetchEntry[]; count: number };
  return { kind: "fresh", vocab: body.vocab, etag, count: body.count };
}

export interface AuthUser {
  id: string;
  email: string;
  role: "user" | "admin";
  /** ISO timestamp when this user proved control of their email. null = nag-banner. */
  email_verified_at: string | null;
}

export const auth = {
  login(email: string, password: string): Promise<{ user: AuthUser }> {
    return call("/auth/login", { method: "POST", body: { email, password } });
  },
  register(email: string, password: string, invite: string): Promise<{ user: AuthUser }> {
    return call("/auth/register", { method: "POST", body: { email, password, invite } });
  },
  logout(): Promise<void> {
    return call("/auth/logout", { method: "POST" });
  },
  me(): Promise<{ user: AuthUser }> {
    return call("/auth/me", { method: "GET" });
  },
  forgot(email: string): Promise<{ ok: true }> {
    return call("/auth/forgot", { method: "POST", body: { email } });
  },
  reset(token: string, password: string): Promise<{ user: AuthUser }> {
    return call("/auth/reset", { method: "POST", body: { token, password } });
  },
  verify(token: string): Promise<{ alreadyVerified: boolean }> {
    return call("/auth/verify", { method: "POST", body: { token } });
  },
};

export interface ServerSettings {
  theme: ThemePref;
  sound: boolean;
  learningDirection: LearningDirection;
  learningMode: LearningMode;
}

export interface ServerHistoryEntry {
  ts: string;
  english: string;
  wasCorrect: boolean;
  questionIndex: number;
}

export interface MeProgressResponse {
  progress: ProgressRecord[];
  history: ServerHistoryEntry[];
  settings: ServerSettings;
  /** v0.12.0+ (Issue #123). Englische Wörter, die der User markiert hat. */
  markers: string[];
  serverTs: string;
}

export interface MarkerPayload {
  english: string;
  marked: boolean;
}

export interface AnswerPayload {
  english: string;
  direction: Direction;
  wasCorrect: boolean;
  questionIndex: number;
  ts: string;
}

export const me = {
  getProgress(): Promise<MeProgressResponse> {
    return call("/me/progress", { method: "GET" });
  },
  postAnswer(payload: AnswerPayload): Promise<{ progress: ProgressRecord; history: ServerHistoryEntry }> {
    return call("/me/progress/answer", { method: "POST", body: payload });
  },
  reset(): Promise<void> {
    return call("/me/progress/reset", { method: "POST" });
  },
  patchSettings(patch: Partial<ServerSettings>): Promise<{ settings: ServerSettings }> {
    return call("/me/settings", { method: "PATCH", body: patch });
  },
  /** Issue #123: setzt oder entfernt einen Marker. Idempotent. Server gibt
   *  den finalen State zurück; 404 wenn das Wort nicht in der aktiven
   *  Vokabel-Liste ist. */
  setMarker(payload: MarkerPayload): Promise<MarkerPayload> {
    return call("/me/marker", { method: "PUT", body: payload });
  },
  /** DSGVO self-delete. Server soft-deletes the account, cascades sessions /
   *  progress / history, and clears the session cookie. */
  deleteAccount(): Promise<void> {
    return call("/me/delete", { method: "POST" });
  },
  /** Request a new verification mail. Rate-limited (3/h per user) server-side. */
  requestVerifyMail(): Promise<void> {
    return call("/me/verify/resend", { method: "POST" });
  },
  /** Correct a typoed registration email. Only for unverified accounts.
   *  Server invalidates outstanding verify tokens, sends a fresh welcome
   *  mail to the new address. Shares the resend rate-limit budget. */
  changeEmail(newEmail: string): Promise<{ user: AuthUser }> {
    return call("/me/email/change", { method: "POST", body: { email: newEmail } });
  },
  /** Fire-and-forget telemetry for the admin completion funnel (#91).
   *  Silent fail on network/auth issues — we never want a broken event to
   *  block the certificate view or print. */
  recordCertificateEvent(kind: "view" | "print"): Promise<void> {
    return call("/me/certificate/event", { method: "POST", body: { kind } });
  },
};

export interface InviteRow {
  code: string;
  name: string;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  /** Multi-use limit since #39. 0 = unlimited; N > 0 = closes after N redemptions. */
  max_uses: number;
  /** Current count of redemptions (joined from invite_redemptions). */
  redemption_count: number;
  /** Most-recent redemption ts, null if never redeemed. */
  last_redeemed_at: string | null;
  /** Legacy single-use mirror — populated only on the FIRST redemption.
   *  Kept for backward-compat readers; new code uses redemption_count. */
  redeemed_by: string | null;
  redeemed_at: string | null;
}

export interface AdminUserRow {
  id: string;
  email: string;
  role: "user" | "admin";
  status: "active" | "blocked";
  created_at: string;
  last_login_at: string | null;
  /** Invite the user redeemed at registration, or null. */
  invite: { code: string; name: string } | null;
  /** True for the row matching the calling admin. */
  self: boolean;
  /** Lifetime history rows for this user (#40). */
  answers_total: number;
  /** Distinct UTC calendar days the user has answered on (#40). */
  active_days: number;
  /** Most recent history.ts, or null (#40). */
  last_activity_at: string | null;
}

export interface UsersPage {
  users: AdminUserRow[];
  total: number;
  page: number;
  pageSize: number;
}

export type UserSortKey =
  | "created_at"
  | "email"
  | "role"
  | "status"
  | "last_login_at"
  | "answers_total"
  | "last_activity_at";

export interface StatsTiles {
  active_today: number;
  active_7d: number;
  active_30d: number;
  answers_total: number;
  avg_answers_per_active_user_7d: number;
  total_ever: number;
  current_active: number;
}

export interface DailyAnswerPoint {
  date: string;
  correct: number;
  wrong: number;
}

export interface HourlyAnswerPoint {
  hour: number;
  total: number;
}

export interface InviteDonutSlice {
  code: string;
  name: string;
  count: number;
}

export interface UserHistogramBucket {
  bucket: string;
  count: number;
}

export interface StatsSnapshot {
  tiles: StatsTiles;
  daily_answers: DailyAnswerPoint[];
  hourly_distribution: HourlyAnswerPoint[];
  invite_donut: InviteDonutSlice[];
  user_histogram: UserHistogramBucket[];
}

export type DailyWindow = 30 | 90 | 365;

export interface CompletionStatsSnapshot {
  /** Legacy alias = enDeCompletedUserCount. Behalten bis das UI ganz auf
   *  die drei direction-aware Felder umgestellt ist. */
  completedUserCount: number;
  /** User mit allen aktiven Vokabeln in EN→DE mastered. Issue #95 Phase 5. */
  enDeCompletedUserCount: number;
  /** User mit allen aktiven Vokabeln in DE→EN mastered. Issue #95 Phase 5. */
  deEnCompletedUserCount: number;
  /** User die BEIDE Richtungen komplett mastered haben (Master-Beender). */
  masterCompletedUserCount: number;
  certificateViewerCount: number;
  certificateViewerCountToday: number;
  certificatePrintCount: number;
  certificatePrintCountToday: number;
  totalActiveUserCount: number;
}

export interface ListUsersParams {
  page?: number;
  pageSize?: number;
  sort?: UserSortKey;
  sortDir?: "asc" | "desc";
  q?: string;
  inviteCode?: string;
}

export interface AdminVocabRow {
  english: string;
  german: string;
  letter: string;
  abbreviation: string | null;
  examples: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface VocabPayload {
  english?: string;
  german: string;
  abbreviation?: string | null;
  examples: string[];
}

export const admin = {
  invites: {
    list(): Promise<{ invites: InviteRow[] }> {
      return call("/admin/invites", { method: "GET" });
    },
    /** maxUses: 1 (default single-use), N>1 (multi-use cap), null (unlimited). */
    create(name: string, maxUses?: number | null): Promise<{ invite: InviteRow }> {
      const body: { name: string; maxUses?: number | null } = { name };
      if (maxUses !== undefined) body.maxUses = maxUses;
      return call("/admin/invites", { method: "POST", body });
    },
    expire(code: string): Promise<void> {
      return call(`/admin/invites/${encodeURIComponent(code)}/expire`, { method: "POST" });
    },
    revoke(code: string): Promise<void> {
      return call(`/admin/invites/${encodeURIComponent(code)}`, { method: "DELETE" });
    },
  },
  users: {
    list(params: ListUsersParams = {}): Promise<UsersPage> {
      const usp = new URLSearchParams();
      if (params.page !== undefined) usp.set("page", String(params.page));
      if (params.pageSize !== undefined) usp.set("pageSize", String(params.pageSize));
      if (params.sort) usp.set("sort", params.sort);
      if (params.sortDir) usp.set("sortDir", params.sortDir);
      if (params.q) usp.set("q", params.q);
      if (params.inviteCode) usp.set("invite_code", params.inviteCode);
      const qs = usp.toString();
      return call(`/admin/users${qs ? `?${qs}` : ""}`, { method: "GET" });
    },
    block(id: string): Promise<void> {
      return call(`/admin/users/${encodeURIComponent(id)}/block`, { method: "POST" });
    },
    unblock(id: string): Promise<void> {
      return call(`/admin/users/${encodeURIComponent(id)}/unblock`, { method: "POST" });
    },
    softDelete(id: string): Promise<void> {
      return call(`/admin/users/${encodeURIComponent(id)}`, { method: "DELETE" });
    },
  },
  stats: {
    get(dailyDays: DailyWindow = 30): Promise<StatsSnapshot> {
      return call(`/admin/stats?dailyDays=${dailyDays}`, { method: "GET" });
    },
    completion(): Promise<CompletionStatsSnapshot> {
      return call("/admin/stats/completion", { method: "GET" });
    },
  },
  vocab: {
    list(): Promise<{ vocab: AdminVocabRow[] }> {
      return call("/admin/vocab", { method: "GET" });
    },
    create(payload: VocabPayload): Promise<{ vocab: AdminVocabRow }> {
      return call("/admin/vocab", { method: "POST", body: payload });
    },
    update(english: string, payload: Omit<VocabPayload, "english">): Promise<{ vocab: AdminVocabRow }> {
      return call(`/admin/vocab/${encodeURIComponent(english)}`, {
        method: "PATCH",
        body: payload,
      });
    },
    softDelete(english: string): Promise<void> {
      return call(`/admin/vocab/${encodeURIComponent(english)}`, { method: "DELETE" });
    },
    restore(english: string): Promise<{ vocab: AdminVocabRow }> {
      return call(`/admin/vocab/${encodeURIComponent(english)}/restore`, { method: "POST" });
    },
    reset(): Promise<{ summary: VocabResetSummary }> {
      return call("/admin/vocab/reset", { method: "POST" });
    },
    generateExamples(english: string, german: string): Promise<{ examples: [string, string] }> {
      return call("/admin/vocab/generate-examples", {
        method: "POST",
        body: { english, german },
      });
    },
  },
  anthropicUsage: {
    get(): Promise<AnthropicUsageSnapshot> {
      return call("/admin/vocab/anthropic-usage", { method: "GET" });
    },
  },
};

export interface AnthropicUsageBucket {
  inputTokens: number;
  outputTokens: number;
  estCostUsd: number;
  callCount: number;
}

export interface AnthropicUsageCall {
  id: number;
  ts: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  estCostUsd: number;
  calledByUserId: string | null;
  context: string | null;
}

export interface AnthropicUsageSnapshot {
  today: AnthropicUsageBucket;
  last7d: AnthropicUsageBucket;
  last30d: AnthropicUsageBucket;
  lifetime: AnthropicUsageBucket;
  recent: AnthropicUsageCall[];
}

export interface VocabResetSummary {
  vocabBefore: number;
  vocabAfter: number;
  orphanProgress: number;
  orphanHistory: number;
}
